import {Button, Checkbox, Col, Collapse, Divider, Radio, Row, Space} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {isEqual} from 'underscore';
import {useGetCollectionFilterOptionsQuery} from "@mwServices/User";
import {formatGrade, formatNumber, formatTitleCase} from "@lib/format";
import {defaultIssueFilterProps, fixFilterObjectEmptyArrays} from "@components/issues-filter-drawer/IssueFilterDrawer";
import {IssueFilterDrawer} from "@components/issues-filter-drawer";
import {FilterIcon} from "@components/icons/Icons";
import {useGetBoxesQuery} from "@mwServices/Boxes";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {SCREEN, useWindowSize} from "@components/window-size-provider";
import {transformEra} from "@lib/utils";

const { Panel } = Collapse;

const MyCollectionFiltersContent = ({showSeries, filters, setFilters, showBox, showOverviewInfo}) => {
    const subscriptionType = useSubscriptionType();
    const showPriceInfo = useMemo(() => ( subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const allowBoxes = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);

    const queryFilters = useMemo(() => ({
        series: showSeries ? [showSeries.series_id] : undefined,
        boxes: showBox ? [showBox.id] : undefined,
    }), [showSeries, showBox]);

    const filterOptionsQuery = useGetCollectionFilterOptionsQuery(queryFilters);

    const locations = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data.locations;
        return [];
    }, [filterOptionsQuery]);

    const gradedBy = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data.gradedBy;
        return [];
    }, [filterOptionsQuery]);

    const grades = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data.grades;
        return [];
    }, [filterOptionsQuery]);

    const publishers = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data.publishers;
        return [];
    }, [filterOptionsQuery]);

    const formats = useMemo(() => {
        if(filterOptionsQuery.data){
            return filterOptionsQuery.data.seriesFormats.filter(format => format.format !== 'error');
        }
        return [];
    }, [filterOptionsQuery]);

    const isSeriesView = useMemo(() => {
        return !!showSeries;
    }, [showSeries]);

    const eras = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data.eras;
        return [];
    }, [filterOptionsQuery]);

    const valuesShown = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data.valuesShown;
        return {covrprice: 0, user: 0};
    }, [filterOptionsQuery]);

    const getItSlabbed = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data.getItSlabbed;
        return 0;
    }, [filterOptionsQuery]);

    const boxesQuery = useGetBoxesQuery({filters: {}});
    const boxes = useMemo(() => {
        if(boxesQuery.data) return boxesQuery.data.boxes;
        return [];
    }, [boxesQuery]);

    const getGradedByCheckbox = (gradedByItem) => {
        const item = gradedBy.find(check => check.graded_by === gradedByItem);
        return item ? (
            <Checkbox
                checked={!filters.gradedBy || filters.gradedBy.indexOf(gradedByItem) !== -1}
                onChange={(event) => {
                    if(!filters.gradedBy && !event.target.checked) {
                        setFilters({...filters, gradedBy: gradedBy.map(item => item.graded_by).filter(check => check !== item.graded_by)});
                    }else if(filters.gradedBy) {
                        let arrGradedBy: any = filters.gradedBy ? [...filters.gradedBy] : [];
                        if (event.target.checked) {
                            arrGradedBy = [...arrGradedBy, item.graded_by]
                        } else {
                            arrGradedBy = !!filters.gradedBy ? filters.gradedBy.filter(check => check !== item.graded_by) : [];
                        }
                        if (arrGradedBy.length === gradedBy.length) arrGradedBy = undefined;
                        setFilters({...filters, gradedBy: arrGradedBy});
                    }
                }}
            >{item.graded_by} ({formatNumber(item.count)})</Checkbox>
        ):(
            <Checkbox disabled>{gradedByItem} ({0})</Checkbox>
        );
    };

    return (
        <>
            <Row>
                <Col span={24}>
                    {isSeriesView && (<Button className='btn-price-guide' type="link" href={`${showSeries.series_permalink}`} >Go To Full Series &gt;</Button>)}
                    <div className="font-18 font-bold">Sort by:</div>
                    <br/>

                    <Radio.Group
                        className="pad-left-10"
                        onChange={(event) => setFilters({...filters, sort: event.target.value})}
                        value={filters.sort}
                    >
                        <Space direction="vertical">
                            <Radio value={0} >Title A-Z</Radio>

                            <Radio value={showPriceInfo ? 1 : null} disabled={!showPriceInfo}>Est Value (Highest to Lowest)</Radio>
                            <Radio value={showPriceInfo ? 2 : null} disabled={!showPriceInfo}>Current Market Trend (%) (Highest to Lowest)</Radio>
                            <Radio value={showPriceInfo ? 9 : null} disabled={!showPriceInfo}>My Profit by $ (Highest to Lowest)</Radio>
                            <Radio value={showPriceInfo ? 3 : null} disabled={!showPriceInfo}>My Profit by % (Highest to Lowest)</Radio>
                            <Radio value={showPriceInfo ? 4 : null} disabled={!showPriceInfo}>Recent Market Sales (Newest to Oldest)</Radio>
                            <Radio value={5} >Grade (Highest to Lowest)</Radio>
                            <Radio value={19}>Recently Added to Collection (Newest to Oldest)</Radio>
                            <Radio value={6} >Comic Publish Date (Newest to Oldest)</Radio>
                        </Space>
                    </Radio.Group>

                    <br/>
                    {!showPriceInfo && (<p><br/><a className="upgrade-link" href="/plans/unlimited/">*Upgrade to use all filter options.</a></p>)}
                </Col>
            </Row>
            <Divider />
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Filter by:</div>

                    <Collapse className="filtersAccordion" ghost defaultActiveKey={[1,2,3,4]} expandIconPosition={"end"}>

                        {!showBox && (
                            <Panel header="Virtual Boxes" key={1} >
                                {allowBoxes ? (
                                    <Space direction="vertical">
                                        <Checkbox
                                            checked={!filters.boxes}
                                            onChange={(event) => {
                                                setFilters({...filters, boxes: event.target.checked ? undefined : []})
                                            }}
                                        >All</Checkbox>
                                        {boxes.map((box,i) => (
                                            <Checkbox
                                                key={i}
                                                checked={!filters.boxes || filters.boxes.indexOf(box.id) !== -1}
                                                onChange={(event) => {
                                                    if(!filters.boxes && !event.target.checked) {
                                                        setFilters({...filters, boxes: boxes.map(item => item.id).filter(item => item !== box.id)});
                                                    }else if(filters.boxes) {
                                                        let filteredBoxes: any = filters.boxes ? [...filters.boxes] : [];
                                                        if (event.target.checked) {
                                                            filteredBoxes = [...filteredBoxes, box.id]
                                                        } else {
                                                            filteredBoxes = filters.boxes ? filters.boxes.filter(item => item !== box.id) : [];
                                                        }
                                                        if (filteredBoxes.length === boxes.length) filteredBoxes = undefined;
                                                        setFilters({...filters, boxes: filteredBoxes});
                                                    }
                                                }}
                                            >{box.name}</Checkbox>
                                        ))}
                                    </Space>
                                ) : (
                                    <a className="upgrade-link" href="/plans/unlimited/">Upgrade to use virtual boxes</a>
                                )}
                            </Panel>
                        )}

                        <Panel header="Storage Location / Box" key={2}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.locations}
                                    onChange={(event) => {
                                        setFilters({...filters, locations: event.target.checked ? undefined : []})
                                    }}
                                >All</Checkbox>
                                {locations.map((location,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.locations || filters.locations.indexOf(location.location) !== -1}
                                        onChange={(event) => {
                                            if(!filters.locations && !event.target.checked) {
                                                setFilters({...filters, locations: locations.map(item => item.location).filter(item => item !== location.location)});
                                            }else if(filters.locations) {
                                                let filtered: any = filters.locations ? [...filters.locations] : [];
                                                if (event.target.checked) {
                                                    filtered = [...filtered, location.location]
                                                } else {
                                                    filtered = filters.locations ? filters.locations.filter(item => item !== location.location) : [];
                                                }
                                                if (filtered.length === locations.length) filtered = undefined;
                                                setFilters({...filters, locations: filtered});
                                            }
                                        }}
                                    >{location.location} ({formatNumber(location.count)})</Checkbox>
                                ))}
                            </Space>
                        </Panel>

                        <Panel header="Hot / Key / Rare" key={3}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={filters.isHot}
                                    onChange={() => setFilters({...filters, isHot: !filters.isHot})}
                                >Hot ({showOverviewInfo && (showOverviewInfo.items_hot)})</Checkbox>
                                <Checkbox
                                    checked={filters.isKey}
                                    onChange={() => setFilters({...filters, isKey: !filters.isKey})}
                                >Key ({showOverviewInfo && (showOverviewInfo.items_key)})</Checkbox>
                                <Checkbox
                                    checked={filters.isRare}
                                    onChange={() => setFilters({...filters, isRare: !filters.isRare})}
                                >Rare ({showOverviewInfo && (showOverviewInfo.items_rare)})</Checkbox>
                            </Space>
                        </Panel>

                        <Panel header="Raw/Slabbed" key={4}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.gradedBy}
                                    onChange={() => {
                                        setFilters({...filters, gradedBy: filters.gradedBy ? undefined : []})
                                    }}
                                >All</Checkbox>
                                {getGradedByCheckbox('RAW')}
                                {getGradedByCheckbox('CGC')}
                                {getGradedByCheckbox('CBCS')}
                                {getGradedByCheckbox('PSA')}
                                {getGradedByCheckbox('PGX')}
                                {getGradedByCheckbox('EGS')}
                                {getGradedByCheckbox('BECKETT')}
                                {getGradedByCheckbox('HALO')}
                                {getGradedByCheckbox('OTHER')}
                            </Space>
                        </Panel>
                        <Panel header="Grade" key={5}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.grades}
                                    onChange={(event) => {
                                        setFilters({...filters, grades: event.target.checked ? undefined : []})
                                    }}
                                >All</Checkbox>
                                {grades.map((grade,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.grades || filters.grades.indexOf(grade.grade) !== -1}
                                        onChange={(event) => {
                                            if(!filters.grades && !event.target.checked) {
                                                setFilters({...filters, grades: grades.map(item => item.grade).filter(item => item !== grade.grade)});
                                            }else if(filters.grades) {
                                                let filtered: any = filters.grades ? [...filters.grades] : [];
                                                if (event.target.checked) {
                                                    filtered = [...filtered, grade.grade]
                                                } else {
                                                    filtered = !!filters.grades ? filters.grades.filter(item => item !== grade.grade) : [];
                                                }
                                                if (filtered.length === grades.length) filtered = undefined;
                                                setFilters({...filters, grades: filtered});
                                            }
                                        }}
                                    >
                                        {formatGrade(grade.grade)} ({formatNumber(grade.count)})
                                    </Checkbox>
                                ))}

                            </Space>
                        </Panel>

                        <Panel header="Era" key={8}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.multipleDateRanges}
                                    onChange={(event) => {
                                        setFilters({...filters, multipleDateRanges: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {eras.map((era, i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.multipleDateRanges || filters.multipleDateRanges.findIndex(dr => !!dr.id && dr.id == era.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.multipleDateRanges && !event.target.checked){
                                                setFilters({...filters, multipleDateRanges: eras.map(transformEra).filter(item => item.id !== era.id)});
                                            } else if(filters.multipleDateRanges) {
                                                let filterDateRanges: any = filters.multipleDateRanges ? [...filters.multipleDateRanges] : [];
                                                if (event.target.checked) {
                                                    filterDateRanges = [...filterDateRanges, transformEra(era)];
                                                } else {
                                                    filterDateRanges = !!filters.multipleDateRanges ? filters.multipleDateRanges.filter(item => !!item.id && item.id !== era.id) : [];
                                                }
                                                if (filterDateRanges.length === eras.length) filterDateRanges = undefined;
                                                setFilters({...filters, multipleDateRanges: filterDateRanges});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(era.name, 'Unknown Era')} ({era.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>

                        <Panel header="Publisher" key={6}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.publishers}
                                    onChange={(event) => {
                                        setFilters({...filters, publishers: event.target.checked ? undefined : []})
                                    }}
                                >All</Checkbox>
                                {publishers.map((publisher,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.publishers || filters.publishers.indexOf(publisher.publisher_id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.publishers && !event.target.checked) {
                                                setFilters({...filters, publishers: publishers.map(item => item.publisher_id).filter(item => item !== publisher.publisher_id)});
                                            }else if(filters.publishers) {
                                                let filtered: any = filters.publishers ? [...filters.publishers] : [];
                                                if (event.target.checked) {
                                                    filtered = [...filtered, publisher.publisher_id]
                                                } else {
                                                    filtered = !!filters.publishers ? filters.publishers.filter(item => item !== publisher.publisher_id) : [];
                                                }
                                                if (filtered.length === publishers.length) filtered = undefined;
                                                setFilters({...filters, publishers: filtered});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(publisher.publisher, 'Unknown Publisher')} ({formatNumber(publisher.total)})
                                    </Checkbox>
                                ))}

                            </Space>
                        </Panel>
                        <Panel header="Values Shown" key={7}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.showValuesShown}
                                    onChange={(event) => {
                                        setFilters({...filters, showValuesShown: event.target.checked ? undefined : -1})
                                    }}
                                >All</Checkbox>
                                {valuesShown && (
                                    <>
                                        <Checkbox
                                            checked={!filters.showValuesShown || filters.showValuesShown === 1}
                                            onChange={(event) => {
                                                const showValuesShown = event.target.checked ? filters.showValuesShown === 2 ? undefined : 1 :
                                                    filters.showValuesShown === 1 ? 1 : 2;
                                                setFilters({...filters, showValuesShown})
                                            }}
                                        >CovrPrice FMV ({formatNumber(valuesShown.covrprice)})</Checkbox>
                                        <Checkbox
                                            checked={!filters.showValuesShown || filters.showValuesShown === 2}
                                            onChange={(event) => {
                                                const showValuesShown = event.target.checked ? filters.showValuesShown === 1 ? undefined : 2 :
                                                    filters.showValuesShown === 2 ? undefined : 1;
                                                setFilters({...filters, showValuesShown})
                                            }}
                                        >My Value ({formatNumber(valuesShown.user)})</Checkbox>
                                    </>
                                )}
                            </Space>
                        </Panel>

                        {!showSeries && (
                            <Panel header="Series Type" key={9}>
                                <Space direction="vertical">
                                    <Checkbox
                                        checked={!filters.formats}
                                        onChange={(event) => {
                                            setFilters({...filters, formats: event.target.checked ? undefined : []})
                                        }}
                                    >All</Checkbox>
                                    {formats.map((format,i) => (
                                        <Checkbox
                                            key={i}
                                            checked={!filters.formats || filters.formats.indexOf(format.format) !== -1}
                                            onChange={(event) => {
                                                if(!filters.formats && !event.target.checked) {
                                                    setFilters({...filters, formats: formats.map(item => item.format).filter(item => item !== format.format)});
                                                }else if(filters.formats) {
                                                    let filtered: any = filters.formats ? [...filters.formats] : [];
                                                    if (event.target.checked) {
                                                        filtered = [...filtered, format.format]
                                                    } else {
                                                        filtered = !!filters.formats ? filters.formats.filter(item => item !== format.format) : [];
                                                    }
                                                    if (filtered.length === formats.length) filtered = undefined;
                                                    setFilters({...filters, formats: filtered});
                                                }
                                            }}
                                        >
                                            {formatTitleCase(format.format, 'Unknown Format')}
                                        </Checkbox>
                                    ))}
                                </Space>
                            </Panel>
                        )}

                        <Panel header="Get It Slabbed" key={10}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={filters.getItSlabbed}
                                    onChange={() => setFilters({...filters, getItSlabbed: filters.getItSlabbed ? undefined : true})}
                                >Get It Slabbed ({getItSlabbed})</Checkbox>
                            </Space>
                        </Panel>

                        <Panel header="Signed/Autographed" key={11}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={filters.isSigned === undefined}
                                    onChange={(event) => setFilters({...filters, isSigned: event.target.checked ? undefined : true})}
                                >All</Checkbox>
                                <Checkbox
                                    checked={filters.isSigned}
                                    onChange={(event) => setFilters({...filters, isSigned: event.target.checked ? true : false})}
                                >Signed</Checkbox>
                                <Checkbox
                                    checked={filters.isSigned === false}
                                    onChange={(event) => setFilters({...filters, isSigned: event.target.checked ? false : true})}
                                >Unsigned</Checkbox>
                            </Space>
                        </Panel>
                    </Collapse>

                </Col>
            </Row>
        </>
    );
};

const MyCollectionFiltersDrawer = ({showSeries, filters, setFilters, showBox, showOverviewInfo}) => {
    const windowDimensions = useWindowSize();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [newFilters, setNewFilters] = useState(filters);

    useEffect(() => {
        if(!isEqual(newFilters, filters)) {
            setNewFilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onClose = () => {
        setNewFilters(filters);
        setShowFilters(false);
    };

    return (
        <>
            <Button className="greyButton" size={"large"} onClick={() => setShowFilters(true)} >
                {windowDimensions.up(SCREEN.SM) && (<span className="filterButtonText">Filter / Sort</span>)}
                <FilterIcon className="font-12" />
            </Button>

            <IssueFilterDrawer visible={showFilters} onClose={onClose} onSearch={() => {
                setShowFilters(false);

                const fixedFilters = fixFilterObjectEmptyArrays(
                    newFilters,
                    filters,
                    ['boxes', 'locations', 'gradedBy', 'grades', 'publishers', 'formats', 'multipleDateRanges']);

                if(fixedFilters.showValuesShown === -1) fixedFilters.showValuesShown = undefined;

                if(!isEqual(fixedFilters, filters)) {
                    setFilters(fixedFilters);
                }else if(!isEqual(fixedFilters, newFilters)) {
                    setNewFilters(fixedFilters);
                }
            }}>
                <MyCollectionFiltersContent showSeries={showSeries} filters={newFilters} setFilters={setNewFilters} showBox={showBox} showOverviewInfo={showOverviewInfo} />
            </IssueFilterDrawer>
        </>
    )
};

MyCollectionFiltersDrawer.defaultProps = defaultIssueFilterProps;

export default MyCollectionFiltersDrawer;